import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { Box, CircularProgress, IconButton } from '@mui/joy';
import { AnimatePresence } from 'framer-motion';
import { NotFound } from '../ServerError/ServerError';
import { CaretDown } from '@phosphor-icons/react';

function ChatMessages({ messages, Bubble, onEdit, onDelete, onReply, isGroup = false, bookmark = false, blocked = false, currentUserId = null, onLoadOlder = null, hasMoreOlder = false, loadingOlder = false, jumpToMessageId = null }) {
    const scrollRef = useRef(null);
    const prevLengthRef = useRef(0);
    const initialScrollDone = useRef(false);
    const [showScrollBtn, setShowScrollBtn] = useState(false);

    // При подгрузке истории сверху сохраняем «расстояние от низа», чтобы вернуть
    // позицию после вставки старых сообщений (иначе вид прыгает).
    const prependRestoreRef = useRef(null);

    const scrollToBottom = useCallback((smooth = false) => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: smooth ? 'smooth' : 'instant',
        });
    }, []);

    // Скролл вниз при первой загрузке сообщений
    useEffect(() => {
        if (!initialScrollDone.current && messages?.length > 0) {
            scrollToBottom(false);
            initialScrollDone.current = true;
        }
    }, [messages, scrollToBottom]);

    // Скролл вниз при новом сообщении
    useEffect(() => {
        const currentLength = messages?.length ?? 0;
        const prevLength = prevLengthRef.current;

        if (currentLength > prevLength && initialScrollDone.current) {
            const el = scrollRef.current;
            if (!el) return;
            const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
            if (isNearBottom) scrollToBottom(true);
        }

        prevLengthRef.current = currentLength;
    }, [messages, scrollToBottom]);

    // Переход из поиска по сообщениям: как только нужное облачко оказалось в
    // списке, прокручиваемся к нему и подсвечиваем. Ref-флаг — чтобы не
    // повторять при каждом обновлении переписки (новое сообщение, прочтение).
    const jumpedRef = useRef(false);
    useEffect(() => {
        if (!jumpToMessageId || jumpedRef.current || !messages?.length) return;

        const el = scrollRef.current?.querySelector(`[data-message-id="${jumpToMessageId}"]`);
        // Сообщение ещё не подгружено — попробуем снова, когда догрузится история.
        if (!el) return;

        jumpedRef.current = true;
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.animate?.(
            [
                { backgroundColor: 'transparent' },
                { backgroundColor: 'var(--joy-palette-primary-softBg)' },
                { backgroundColor: 'transparent' },
            ],
            { duration: 1600, easing: 'ease-in-out' }
        );
    }, [jumpToMessageId, messages]);

    // Восстановление позиции после вставки старых сообщений сверху
    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el || prependRestoreRef.current == null) return;
        el.scrollTop = el.scrollHeight - prependRestoreRef.current;
        prependRestoreRef.current = null;
    }, [messages]);

    const handleScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        setShowScrollBtn(distanceFromBottom > 200);

        // Близко к верху — подгружаем историю. Запоминаем расстояние от низа,
        // чтобы useLayoutEffect вернул позицию после префиксной вставки.
        if (
            onLoadOlder &&
            hasMoreOlder &&
            !loadingOlder &&
            prependRestoreRef.current == null &&
            initialScrollDone.current &&
            el.scrollTop <= 80
        ) {
            prependRestoreRef.current = el.scrollHeight - el.scrollTop;
            onLoadOlder();
        }
    };

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
            {loadingOlder && (
                <Box sx={{ position: 'absolute', top: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 10, pointerEvents: 'none' }}>
                    <CircularProgress size="sm" />
                </Box>
            )}
            <Box
                ref={scrollRef}
                onScroll={handleScroll}
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    px: 2,
                    pt: 2,
                    pb: 12,
                    overflowY: 'auto',
                    gap: '12px',
                    // Скрываем вертикальный индикатор прокрутки, сам скролл работает
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                <AnimatePresence initial={false}>
                    {messages.length > 0 && messages.map((msg, index) => (
                        <Bubble
                            key={`${msg?.id ?? msg?.content}-${index}`}
                            {...msg}
                            isGroup={isGroup}
                            currentUserId={currentUserId}
                            onEdit={(text) => onEdit?.(msg.id, text)}
                            onDelete={(deleteType) => onDelete?.(msg.id, deleteType)}
                            onReply={(replyData) => onReply?.(replyData)}
                            bookmark={bookmark ?? false}
                            blocked={blocked ?? false}
                        />
                    ))}
                    {messages.length === 0 && (
                        <NotFound img="no-messages.svg" title="Начните общение прямо сейчас!" />
                    )}
                </AnimatePresence>
            </Box>

            <IconButton
                onClick={() => scrollToBottom(true)}
                variant="soft"
                color="neutral"
                sx={{
                    position: 'absolute',
                    bottom: '90px',
                    right: '16px',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    zIndex: 10,
                    opacity: showScrollBtn ? 1 : 0,
                    transform: showScrollBtn ? 'translateY(0)' : 'translateY(8px)',
                    pointerEvents: showScrollBtn ? 'auto' : 'none',
                    transition: 'opacity 0.2s ease, transform 0.2s ease',
                    boxShadow: 'var(--joy-shadowRing, 0 0 #000),0px 1px 2px 0px rgba(var(--joy-shadowChannel, 21 21 21) / var(--joy-shadowOpacity, 0.08))'
                }}
            >
                <CaretDown size={20} />
            </IconButton>
        </Box>
    );
}

export default ChatMessages;
